import { ApiMenuData, ApiMenuItem, MenuItem, MenuCategory, MenuOption } from '../types';
import apiService from '../services/apiService';

class MenuService {
  private apiData: ApiMenuData | null = null;
  private isLoading: boolean = false;
  private isLoaded: boolean = false;

  /**
   * Initialize menu data from API
   */
  async initializeMenuData(): Promise<void> {
    if (this.isLoaded || this.isLoading) {
      return;
    }

    this.isLoading = true;
    try {
      this.apiData = await apiService.fetchMenuData();
      this.isLoaded = true;
      console.log('Menu data initialized successfully');
    } catch (error) {
      console.error('Failed to initialize menu data:', error);
      // Fallback to test data is handled in apiService
      this.apiData = apiService.getTestData();
      this.isLoaded = true;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Force refresh menu data from API
   */
  async refreshMenuData(): Promise<void> {
    this.isLoaded = false;
    await this.initializeMenuData();
  }

  /**
   * Check if menu data is available
   */
  isMenuDataAvailable(): boolean {
    return this.apiData !== null && this.isLoaded;
  }

  /**
   * Get loading state
   */
  getLoadingState(): boolean {
    return this.isLoading;
  }

  // Get all unique categories from the data
  getMenuCategories(): MenuCategory[] {
    if (!this.apiData) {
      return [];
    }

    const sectionNames = new Set<string>();
    
    this.apiData.data.forEach(item => {
      if (item.enabled && item.sections.length > 0) {
        item.sections.forEach(section => {
          sectionNames.add(section.name);
        });
      }
    });

    return Array.from(sectionNames)
      .sort()
      .map(name => ({
        id: this.slugify(name),
        name,
        icon: '🍽️' // Single default icon for all categories
      }));
  }

  // Get menu items for a specific category
  getMenuItemsByCategory(categoryId: string): MenuItem[] {
    if (!this.apiData) {
      return [];
    }

    const categoryName = this.getCategoryNameFromId(categoryId);
    
    return this.apiData.data
      .filter(item => 
        item.enabled && 
        item.itemType === 'Item' &&
        item.sections.some(section => section.name === categoryName)
      )
      .map(item => this.transformApiItemToMenuItem(item, categoryId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // Get all menu items
  getAllMenuItems(): MenuItem[] {
    if (!this.apiData) {
      return [];
    }

    const categories = this.getMenuCategories();
    const allItems: MenuItem[] = [];

    categories.forEach(category => {
      const items = this.getMenuItemsByCategory(category.id);
      allItems.push(...items);
    });

    return allItems;
  }

  // Transform API item to app MenuItem
  private transformApiItemToMenuItem(apiItem: ApiMenuItem, categoryId: string): MenuItem {
    return {
      id: apiItem.key.toString(),
      name: apiItem.name,
      displayName: apiItem.displayName,
      description: apiItem.description || '',
      price: apiItem.price,
      image: this.getItemImage(apiItem),
      category: categoryId,
      options: this.transformOptionSets(apiItem.optionSets)
    };
  }

  // Transform option sets to menu options
  private transformOptionSets(optionSets: ApiMenuItem['optionSets']): MenuOption[] {
    return optionSets
      .filter(optionSet => optionSet.enabled)
      .map(optionSet => ({
        id: optionSet.key.toString(),
        name: optionSet.name,
        displayName: optionSet.displayName,
        type: this.getOptionType(optionSet.name),
        required: optionSet.required,
        minSelect: optionSet.setMinSelect,
        maxSelect: optionSet.setMaxSelect,
        choices: optionSet.optionSetItems
          .filter(item => item.enabled)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(item => ({
            id: item.key.toString(),
            name: item.name,
            displayName: item.displayName,
            price: item.price
          }))
      }))
      .sort((a, b) => parseInt(a.id) - parseInt(b.id));
  }

  // Get item image URL
  private getItemImage(apiItem: ApiMenuItem): string {
    if (apiItem.images && apiItem.images.length > 0) {
      const landscapeImage = apiItem.images.find(img => img.imageType === 'LandscapeImage');
      if (landscapeImage) {
        return `https://${landscapeImage.url}`;
      }
      return `https://${apiItem.images[0].url}`;
    }
    
    // Return single default placeholder for all items without images
    return '🍽️';
  }

  // Determine option type from name
  private getOptionType(optionName: string): MenuOption['type'] {
    const name = optionName.toLowerCase();
    if (name.includes('size')) return 'size';
    if (name.includes('drink') || name.includes('beverage')) return 'drink';
    if (name.includes('side') || name.includes('condiment')) return 'side';
    if (name.includes('addon') || name.includes('topping')) return 'addon';
    return 'options';
  }

  // Convert category name to slugified ID
  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '');
  }

  // Convert category ID back to name
  private getCategoryNameFromId(categoryId: string): string {
    const categories = this.getMenuCategories();
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : '';
  }

  // Get first category ID (for initial load)
  getFirstCategoryId(): string {
    const categories = this.getMenuCategories();
    return categories.length > 0 ? categories[0].id : '';
  }
}

export const menuService = new MenuService();
export default menuService; 